import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import TagIcon from '@square-icons/react/16/UI/tag';
import Sentry from 'application/exception_reporter';
import {
  TRACKING_EVENT_TYPES,
  getAddOnByVariantId,
  getAlternativeUrl,
} from 'components/pages/product/helpers';
import AccessoryModal from 'components/pages/product/accessory_modal';
import UpsellAccordion from 'components/pages/product/upsell_accordion';
import RichTextContent from 'components/common/contentful/rich_text_content';
import InfoBubble from 'components/common/info_bubble';
import SubtotalBox from './subtotal_box';
import ProductHeading from './product_heading';
import ProductActions from './product_actions';
import FulfillmentSection from 'typescript/pages/product/fulfillment_section';
import ThreePPContent from './three_p_p_content';
import ProductAlternatives from './product_alternatives';
import ProductClient from 'clients/product';
import { updateCtaSubnav, updateCurrentUrl } from 'utilities/nav';
import { trackEvent, trackCdp } from 'utilities/tracking';
import ProductAddOn, { NO_ADD_ON_VALUE } from './product_add_on';
import ProductPromotionBanner from 'components/common/product_promotion_banner';
import { useFeatureFlags } from 'components/context/feature_flags';
import ColorProductGroup from 'typescript/components/product_groups/color_product_group';
import {
  allProductsAreColorGrouped,
  createColorProductFromProduct,
  filterInvalidColorProductConfigs,
} from 'typescript/utilities/color_group_utils';
import ProductAddOnSkeleton from 'components/pages/product/product_add_on_skeleton';
import { useProductContext } from 'typescript/pages/product/contexts/product_context';

export const useProductOptionStyles = makeStyles((theme) => ({
  configContainer: {
    marginBottom: theme.spacing(4),
    '& > * + *': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  fadeIn: {
    opacity: 0,
    animation: '$fadeIn 0.3s forwards',
  },
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
}));

const ProductOptions = (props) => {
  const {
    setFinancingModalOpen,
    setComparisonModalOpen,
    setBopisPickupOptionsModalOpen,
    alternatives,
    currentAltMetadata,
    appendRelationImages = () => {},
    enableAlternativesModal = false,
  } = props;

  const {
    currentProduct,
    setCurrentProduct,
    selection,
    setSelection,
    addOns,
    isGetAddOnsLoading: isAddOnsLoading,
    isGetAddOnsError: isAddOnsLoadingError,
    cart,
    onUpdateItemQuantity,
    isUpdateItemQuantityLoading,
    scrollToRef,
    mainProductAdded,
    promoEligibility,
    bopisPickupLocation,
    bopisPostalCode,
  } = useProductContext();

  const classes = useProductOptionStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const { show1PPDisclaimerOnPDP, showCustomCtaSubnav } = useFeatureFlags();
  const [accessoryModal, setAccessoryModal] = useState({ open: false });
  const [cardQuantities, setCardQuantities] = useState({});
  const [upsells, setUpsells] = useState([]);

  useEffect(() => {
    if (!currentProduct.featured_product) {
      return;
    }

    const pc = new ProductClient(currentProduct.slug);
    pc.fetchUpsells()
      .then((data) => {
        setUpsells(data);
        const initialCardQuantities = data
          .flatMap((catToUpsell) => catToUpsell.upsells)
          .reduce(
            (hash, upsell) => ({
              [upsell.slug]: 0,
              ...hash,
            }),
            cardQuantities,
          );
        setCardQuantities(initialCardQuantities);
      })
      .catch((e) => Sentry.capture(e));
  }, [currentProduct.slug]);

  const onAlternativeSelected = (value) => {
    if (value === null) {
      return;
    }

    // Track alternative selected events
    const selectedAlternative = alternatives.find((alt) => alt.slug === value);
    trackEvent({
      name: 'Alternative',
      value: 'Change',
      params: {
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: cart?.number,
        ...selectedAlternative.tracking_data,
      },
    });
    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAlternative,
      params: {
        feature: 'shop',
        choice: selectedAlternative.name,
        alternativeUrl: getAlternativeUrl(selectedAlternative.slug),
        cartNumber: cart?.number,
        ...selectedAlternative.tracking_data,
      },
    });

    // Update current window path to selected alternative slug
    window.history.replaceState(
      {},
      null,
      getAlternativeUrl(selectedAlternative.slug),
    );

    // Update PublicWebNav current URL & CTA Subnav
    updateCurrentUrl(getAlternativeUrl(selectedAlternative.slug));
    // Only update legacy CTA subnav when custom React CTA subnav experiment is not active
    if (!showCustomCtaSubnav) {
      updateCtaSubnav(selectedAlternative.cta_subnav);
    }

    setCurrentProduct(selectedAlternative);
    setSelection({ quantity: 1, addOns: {} });
  };

  const onAddOnSelected = ({
    category: addOnCategory,
    newVariantId,
    choice,
  }) => {
    if (newVariantId === null) {
      return;
    }

    if (newVariantId === NO_ADD_ON_VALUE) {
      setSelection((prevSelection) => {
        const newAddOns = { ...prevSelection.addOns };
        delete newAddOns[addOnCategory];
        return { ...prevSelection, addOns: newAddOns };
      });
      appendRelationImages(null);
      return;
    }

    setSelection((prevSelection) => {
      const newAddOns = { ...prevSelection.addOns };
      newAddOns[addOnCategory] = newVariantId;
      return { ...prevSelection, addOns: newAddOns };
    });

    const addOnByVariantId = getAddOnByVariantId(addOns, newVariantId);

    appendRelationImages(addOnByVariantId.product_key);

    // tracking
    const trackingData = addOnByVariantId.tracking_data;
    trackEvent({
      name: 'Add On',
      value: 'Change',
      params: {
        choice,
        cartNumber: cart?.number,
        ...trackingData,
      },
    });
    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAddOn,
      params: {
        feature: 'shop',
        choice,
        cartNumber: cart?.number,
        ...trackingData,
      },
    });
  };

  const onAddToCartClick = () => {
    onUpdateItemQuantity({
      variantToken: currentProduct.variant_token,
      productName: currentProduct.name,
      quantity: selection.quantity,
      addOnIds: Object.values(selection.addOns),
      trackingData: currentProduct.tracking_data,
      bopisLocationId: bopisPickupLocation?.location_id,
      bopisSearchPostalCode: bopisPostalCode,
      source: 'pdp-product-options',
    });
  };

  const renderPromotionBanner = () => (
    <>
      <ProductPromotionBanner
        {...promoEligibility}
        slug={currentProduct.slug}
      />
      {currentProduct.promotion_banner && (
        <InfoBubble icon={<TagIcon />}>
          <RichTextContent
            content={currentProduct.promotion_banner}
            className="medium-20"
          />
        </InfoBubble>
      )}
    </>
  );

  const renderUpsell = () => {
    trackCdp({
      name: `ProductOptions:${TRACKING_EVENT_TYPES.UpsellsRendered}`,
      params: {
        feature: 'shop',
        cartNumber: cart?.number,
        product: currentProduct.tracking_data,
      },
    });

    const rows = upsells.map(({ category_name, upsells: currUpsells }) => {
      let rowTitle = t(`relations.accessoryCategories.${category_name}`);

      // Seeing only an "Other" category looks weird. Call it "Accessories"
      // when it's by itself.
      if (upsells.length === 1 && category_name === 'other') {
        rowTitle = t('relations.accessoryCategories.accessories');
      }

      return (
        <UpsellAccordion
          title={rowTitle}
          upsells={currUpsells}
          image={currUpsells[0].images.tile}
          setAccessoryModal={setAccessoryModal}
          cardQuantities={cardQuantities}
          setCardQuantities={setCardQuantities}
          key={category_name}
        />
      );
    });

    return (
      <Box
        className={classes.configContainer}
        ref={scrollToRef}
        data-testid="upsell-accordion"
      >
        <h4 className="heading-30">
          {t('productPage.upsellAccordionTitle', {
            productName: t(`marketingNames.${currentProduct.marketing_name}`),
          })}
        </h4>
        <Box mb={5}>{rows}</Box>
        <SubtotalBox
          product={currentProduct}
          cart={cart}
          subtotal={cart?.subtotal}
          itemCount={cart?.item_count}
        />
      </Box>
    );
  };

  const renderAlternatives = () => {
    if (allProductsAreColorGrouped([currentProduct, ...alternatives])) {
      return (
        <ColorProductGroup
          selectedProduct={createColorProductFromProduct(currentProduct)}
          products={filterInvalidColorProductConfigs(
            alternatives.map((alternative) =>
              createColorProductFromProduct(alternative),
            ),
          )}
          onProductSelected={(product) => {
            onAlternativeSelected(product.slug);
          }}
        />
      );
    }

    return (
      <ProductAlternatives
        currentProduct={currentProduct}
        alternatives={alternatives}
        currentAltMetadata={currentAltMetadata}
        onAlternativeSelected={onAlternativeSelected}
        onComparisonTriggerClick={() => setComparisonModalOpen(true)}
        enableAlternativesModal={enableAlternativesModal}
      />
    );
  };

  const renderAddOns = () => {
    if (
      isAddOnsLoading ||
      // The request finished loading, but the data hasn't populated yet
      (!isAddOnsLoading &&
        !isAddOnsLoadingError &&
        Object.keys(addOns).length === 0 &&
        currentProduct?.add_ons_category_count > 0)
    ) {
      return (
        <div
          style={{
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(3),
          }}
        >
          {Array.from(
            { length: currentProduct.add_ons_category_count },
            (_, index) => (
              <div key={index} style={{ marginBottom: theme.spacing(2) }}>
                <ProductAddOnSkeleton />
              </div>
            ),
          )}
        </div>
      );
    }

    return (
      <div className={classes.fadeIn}>
        {Object.keys(addOns).length > 0 &&
          Object.entries(addOns).map(([category, addOns]) => (
            <ProductAddOn
              key={category}
              category={category}
              addOns={addOns}
              selected={selection.addOns[category]}
              onSelect={onAddOnSelected}
              currentProduct={currentProduct}
            />
          ))}
      </div>
    );
  };

  return (
    <>
      <AccessoryModal
        {...accessoryModal}
        cardQuantities={cardQuantities}
        setCardQuantities={setCardQuantities}
      />
      <Box className={classes.configContainer}>
        <ProductHeading
          currentProduct={currentProduct}
          onFinancingTriggerClick={() => setFinancingModalOpen(true)}
        />
        {renderPromotionBanner()}
        {renderAlternatives()}
        {renderAddOns()}
        {!(
          currentProduct.shipping.out_of_stock ||
          currentProduct.shipping.no_ship
        ) && (
          <div
            data-testid="shipping-bubble-container"
            style={{ marginTop: theme.spacing(3.5) }}
          >
            <FulfillmentSection
              quote={currentProduct.shipping.ship_quote}
              expeditedAvailable={currentProduct.shipping.expedited_available}
              bopisEligible={
                currentProduct.shipping.bopis_eligible &&
                Object.values(selection.addOns).every((variantId) => {
                  const addOn = getAddOnByVariantId(addOns, variantId);
                  return addOn?.shipping?.bopis_eligible !== false;
                })
              }
              expeditedCost={currentProduct.shipping.expedited_cost}
              bopisPickupOption={bopisPickupLocation}
              onBopisLocationClick={() => setBopisPickupOptionsModalOpen(true)}
            />
          </div>
        )}
        <ProductActions
          currentProduct={currentProduct}
          addOns={addOns}
          selection={selection}
          setSelection={setSelection}
          requesting={isUpdateItemQuantityLoading}
          onAddToCartClick={onAddToCartClick}
          eligiblePromotion={promoEligibility?.eligible}
        />
      </Box>
      {currentProduct.featured_product ? (
        <>
          {/* SHOP-7918: Only display Contentful Disclaimer when FF is on.
            This FF exist because all product's disclaimer fields needs to be audited
            before enabled for all products.
            */}
          {mainProductAdded && renderUpsell()}
          {show1PPDisclaimerOnPDP && currentProduct?.disclaimer && (
            <RichTextContent
              content={currentProduct.disclaimer}
              color={theme.color.gray['55']}
              className="paragraph-20"
            />
          )}
        </>
      ) : (
        <ThreePPContent
          data-testid="3pp-content"
          className={classes.configContainer}
          currentProduct={currentProduct}
        />
      )}
    </>
  );
};

export default ProductOptions;
